"use client";

import Link from "next/link";
import { ChevronLeft, PanelLeftIcon } from "lucide-react";
import { Button } from "@gogive/design-system";
import { useSidebar } from "@gogive/design-system/components/sidebar";
import { PageLayoutBreadcrumbs } from "@/components/PageLayoutBreadcrumbs";
import {
  findSettingsRouteByPathname,
  isChatPath,
  isSettingsPath,
  parseChatConversationId,
} from "@/config/navigation";
import { useConversation } from "@/services/chat";
import { useRouteBreadcrumbs } from "@/hooks/useRouteBreadcrumbs";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { useRenderedTopBarActions } from "@/hooks/useRenderedTopBarActions";
import { useAccount } from "@/hooks/useAccount";
import { StripeConnectTopBarAlert } from "./stripe/StripeConnectNudge";

function MobileNavTrigger() {
  const { openMobile, setOpenMobile } = useSidebar();

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label="Open navigation"
      aria-expanded={openMobile}
      className="size-8 shrink-0 md:hidden"
      onClick={() => setOpenMobile(!openMobile)}
    >
      <PanelLeftIcon className="size-4" />
    </Button>
  );
}

export function TopBar() {
  const pathname = usePathname() ?? "/";
  const { account, isLoading } = useAccount();
  const breadcrumbs = useRouteBreadcrumbs();
  const pageActions = useRenderedTopBarActions();
  const showStripeAlert =
    !isLoading && account && !account.stripeConnected;
  const inSettings = isSettingsPath(pathname);
  const inChat = isChatPath(pathname);
  const chatConversationId = parseChatConversationId(pathname);
  const { conversation: activeConversation } = useConversation(
    inChat ? chatConversationId ?? undefined : undefined,
  );
  const settingsTitle = useMemo(() => {
    if (!inSettings) return null;
    return findSettingsRouteByPathname(pathname)?.name ?? "Settings";
  }, [inSettings, pathname]);
  const chatTitle = useMemo(() => {
    if (!inChat) return null;
    if (!chatConversationId) return "New chat";
    const title = activeConversation?.title?.trim();
    return title || "Chat";
  }, [activeConversation?.title, chatConversationId, inChat]);

  const settingsCatalogBack = useMemo(() => {
    const normalized = pathname.replace(/\/+$/, "") || "/";
    const catalogs: { segment: string; href: string; label: string }[] = [
      {
        segment: "integrations",
        href: "/settings/integrations",
        label: "Integrations",
      },
      {
        segment: "installation",
        href: "/settings/installation",
        label: "Installation",
      },
    ];
    for (const catalog of catalogs) {
      if (!normalized.startsWith(`/settings/${catalog.segment}/`)) {
        continue;
      }
      const segments = normalized.split("/").filter(Boolean);
      if (
        segments.length === 3 &&
        segments[0] === "settings" &&
        segments[1] === catalog.segment
      ) {
        return { href: catalog.href, label: catalog.label };
      }
    }
    return null;
  }, [pathname]);

  return (
    <header
      className="z-50 flex min-h-11 shrink-0 items-center gap-2 border-b border-border bg-card px-4 py-1"
    >
      <MobileNavTrigger />
      {inSettings ? (
        settingsCatalogBack ? (
          <Link
            href={settingsCatalogBack.href}
            className="flex min-w-0 flex-1 items-center gap-1.5 truncate text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="size-4 shrink-0" aria-hidden />
            {settingsCatalogBack.label}
          </Link>
        ) : (
          <h1 className="min-w-0 flex-1 truncate text-sm font-medium">
            {settingsTitle}
          </h1>
        )
      ) : inChat ? (
        <h1 className="min-w-0 flex-1 truncate text-sm font-medium">
          {chatTitle}
        </h1>
      ) : (
        <PageLayoutBreadcrumbs className="min-w-0 flex-1" items={breadcrumbs} />
      )}
      {pageActions ? (
        <div className="flex shrink-0 items-center gap-2">{pageActions}</div>
      ) : null}
      {showStripeAlert ? (
        <div className="hidden min-w-0 max-w-[min(100%,20rem)] shrink lg:flex xl:max-w-md">
          <StripeConnectTopBarAlert />
        </div>
      ) : null}
    </header>
  );
}
